import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { QueryKey } from '@tanstack/react-query';
import type { ShippingLineDTO, UturnUserResponse } from '@uturn/api/v1';
import {
	UserSettingsDtoContainerStatus,
	UserSettingsDtoTransportType,
	useFetchUturnUserById,
	useUpdateUserDetails,
} from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { queryClient } from '@uturn/portal/query';
import { isSelectableShippingLine } from '@uturn/portal/types/shipping-line.typeguard';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	Combobox,
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	RadioGroup,
	RadioGroupItem,
	sonner,
} from '@uturn/ui-kit';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	type EditDefaultValuesFormValues,
	editDefaultValuesSchema,
	requiredFields,
} from './edit-default-values-modal.zod';

// Derived from the schema so the checks below cover every field, including the
// ones the form never wrote a key for.
const settingsFields = editDefaultValuesSchema.keyof().options;

/** A field is "no preference" while it is null (cleared) or undefined (unset). */
function isNoPreference(
	value: EditDefaultValuesFormValues[keyof EditDefaultValuesFormValues],
) {
	return value === undefined || value === null;
}

function hasAnySelection(values: EditDefaultValuesFormValues) {
	return settingsFields.some((field) => !isNoPreference(values[field]));
}

/** True while at least one field is still left at "no preference". */
function hasMissingSelection(values: EditDefaultValuesFormValues) {
	return settingsFields.some((field) => isNoPreference(values[field]));
}

function formatBooleanValue(
	value: boolean | null | undefined,
	[whenTrue, whenFalse]: [string, string],
) {
	if (value === undefined || value === null) {
		return '';
	}

	return value ? whenTrue : whenFalse;
}

export function EditDefaultValuesModal({
	openModal,
	setOpenModal,
	onSuccess,
}: {
	openModal: boolean;
	setOpenModal: (open: boolean) => void;
	onSuccess?: (data: UturnUserResponse) => void;
}) {
	const { shipmentMetaData, metadata } = useContext(MetaDataContext);
	const userId = metadata?.user?.id;

	const { data: user, queryKey } = useFetchUturnUserById(userId ?? -1, {
		query: {
			enabled: userId !== undefined,
			select: (data) => data.data,
		},
	});

	if (user === undefined) {
		return null;
	}

	return (
		<EditDefaultValuesForm
			key={user.id}
			user={user}
			queryKey={queryKey}
			shippingLines={shipmentMetaData?.shippingLines ?? []}
			openModal={openModal}
			setOpenModal={setOpenModal}
			onSuccess={onSuccess}
		/>
	);
}

function EditDefaultValuesForm({
	user,
	queryKey,
	shippingLines,
	openModal,
	setOpenModal,
	onSuccess,
}: {
	user: UturnUserResponse;
	queryKey: QueryKey;
	shippingLines: ShippingLineDTO[];
	openModal: boolean;
	setOpenModal: (open: boolean) => void;
	onSuccess?: (data: UturnUserResponse) => void;
}) {
	const { t } = useTranslation();

	// Set while the "everything cleared" confirmation is open; holds the values
	// to save once the user confirms.
	const [valuesPendingConfirmation, setValuesPendingConfirmation] =
		useState<EditDefaultValuesFormValues | null>(null);

	const form = useForm<EditDefaultValuesFormValues>({
		resolver: zodResolver(editDefaultValuesSchema),
		mode: 'onChange',
		defaultValues: user.settings ?? {},
	});

	const { mutate, isPending } = useUpdateUserDetails({
		mutation: {
			onSuccess: (data) => {
				setOpenModal(false);
				queryClient.invalidateQueries({ queryKey });

				sonner.success(
					t(
						'pages.addshipment.manual.settings.form.submit.success.description',
						'Your prefilled values have been updated!',
					),
				);

				onSuccess?.(data.data);
			},
			onError: () => {
				sonner.error(t('general.try-again-later', 'Please try again later.'));
			},
		},
	});

	const isUpdating = form.formState.isSubmitting || isPending;

	// Radio groups can't be deselected once picked, so offer an explicit way
	// to clear every selection back to "no preference".
	const values = form.watch();
	const hasSelection = hasAnySelection(values);

	const clearSelection = () => {
		const options = { shouldDirty: true, shouldValidate: true } as const;
		form.setValue('shippingLineId', null, options);
		form.setValue('transportType', null, options);
		form.setValue('fixedPrice', null, options);
		form.setValue('requiresVat', null, options);
		form.setValue('containerStatus', null, options);
	};

	const save = (formValues: EditDefaultValuesFormValues) => {
		mutate({
			id: user.id,
			data: {
				...user,
				// Map null -> undefined so a cleared field is omitted from the
				// payload. `?? undefined` only replaces null/undefined, so `false`
				// booleans are preserved.
				settings: {
					shippingLineId: formValues.shippingLineId ?? undefined,
					transportType: formValues.transportType ?? undefined,
					fixedPrice: formValues.fixedPrice ?? undefined,
					requiresVat: formValues.requiresVat ?? undefined,
					containerStatus: formValues.containerStatus ?? undefined,
				},
			},
		});
	};

	const onSubmit = (formValues: EditDefaultValuesFormValues) => {
		// Leaving a field at "no preference" is valid, but the organisation's
		// default applies instead of the field staying empty, so confirm first.
		if (hasMissingSelection(formValues)) {
			setValuesPendingConfirmation(formValues);
			return;
		}

		save(formValues);
	};

	return (
		<Dialog open={openModal} onOpenChange={setOpenModal}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t(
							'pages.addshipment.manual.modal.edit-default-values.heading',
							'Update my prefilled values',
						)}
					</DialogTitle>
					<DialogDescription>
						{t(
							'pages.addshipment.manual.modal.edit-default-values.subheading',
							'Every new shipment you create will use the following values.',
						)}
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<FormField
							control={form.control}
							name="shippingLineId"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											requiredFields.shippingLineId ? 'required-asterix' : ''
										}
									>
										{t(
											'general.default-shipment-values.shipping-line.label',
											'Shipping line',
										)}
									</FormLabel>
									<FormControl>
										<Combobox
											defaultValue={field.value ?? undefined}
											onChange={(value) => field.onChange(value ?? null)}
											options={shippingLines
												.filter(isSelectableShippingLine)
												.map((shippingLine) => ({
													value: shippingLine.id,
													label: `${shippingLine.description} (${shippingLine.code})`,
												}))}
											nullable={true}
											placeholder={t(
												'pages.addshipment.manual.settings.form.shipping-line.placeholder',
												'Select shipping line',
											).toString()}
											searchPlaceholder={t(
												'pages.addshipment.manual.settings.form.shipping-line.dropdown.placeholder',
												'Search shipping line...',
											).toString()}
											emptyMessage={t(
												'pages.addshipment.manual.settings.form.shipping-line.dropdown.no-results',
												'No shipping line found.',
											).toString()}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="transportType"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											requiredFields.transportType ? 'required-asterix' : ''
										}
									>
										{t(
											'general.default-shipment-values.transport-type.label',
											'Transport type',
										)}
									</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											value={field.value ?? ''}
											className="flex flex-col space-y-1"
										>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem
														value={UserSettingsDtoTransportType.IMPORT}
													/>
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.transport-type.values.import',
														'Import',
													)}
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem
														value={UserSettingsDtoTransportType.EXPORT}
													/>
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.transport-type.values.export',
														'Export',
													)}
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem
														value={UserSettingsDtoTransportType.SHUNT}
													/>
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.transport-type.values.shunt',
														'Shunt',
													)}
												</FormLabel>
											</FormItem>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="fixedPrice"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											requiredFields.fixedPrice ? 'required-asterix' : ''
										}
									>
										{t(
											'general.default-shipment-values.carrier-can.label',
											'Carrier can',
										)}
									</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={(value) =>
												field.onChange(value === 'bid-accept')
											}
											value={formatBooleanValue(field.value, [
												'bid-accept',
												'bid-only',
											])}
											className="flex flex-col space-y-1"
										>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="bid-only" />
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.carrier-can.values.bid-only',
														'Bid only',
													)}
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="bid-accept" />
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.carrier-can.values.bid-only-accept',
														'Bid or accept',
													)}
												</FormLabel>
											</FormItem>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="requiresVat"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											requiredFields.requiresVat ? 'required-asterix' : ''
										}
									>
										{t(
											'general.default-shipment-values.goods-cleared.label',
											'Goods cleared',
										)}
									</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={(value) =>
												field.onChange(value === 'regular')
											}
											value={formatBooleanValue(field.value, [
												'regular',
												'free',
											])}
											className="flex flex-col space-y-1"
										>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="free" />
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.goods-cleared.values.free-vat',
														'Free of VAT',
													)}
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="regular" />
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.goods-cleared.values.regular-vat',
														'Regular VAT',
													)}
												</FormLabel>
											</FormItem>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="containerStatus"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											requiredFields.containerStatus ? 'required-asterix' : ''
										}
									>
										{t(
											'general.default-shipment-values.container-status.label',
											'Container status',
										)}
									</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											// Coalesce to '' so the group stays controlled and
											// shows no selection when cleared (undefined would make
											// Radix fall back to uncontrolled and keep the value).
											value={field.value ?? ''}
											className="flex flex-col space-y-1"
										>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem
														value={UserSettingsDtoContainerStatus.FULL}
													/>
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.container-status.values.full',
														'Full',
													)}
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem
														value={UserSettingsDtoContainerStatus.EMPTY}
													/>
												</FormControl>
												<FormLabel className="cursor-pointer font-normal">
													{t(
														'general.default-shipment-values.container-status.values.empty',
														'Empty',
													)}
												</FormLabel>
											</FormItem>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter className="sm:justify-between">
							<Button
								type="button"
								variant="ghost"
								className="text-destructive hover:bg-destructive/10 hover:text-destructive"
								disabled={isUpdating || !hasSelection}
								onClick={clearSelection}
							>
								{t(
									'pages.addshipment.manual.modal.edit-default-value.reset',
									'Reset selection',
								)}
							</Button>
							<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
								<DialogClose asChild>
									<Button type="button" variant="outline">
										{t(
											'pages.addshipment.manual.modal.edit-default-value.cancel',
											'Cancel',
										)}
									</Button>
								</DialogClose>
								<Button
									type="submit"
									disabled={
										isUpdating ||
										!form.formState.isValid ||
										!form.formState.isDirty
									}
								>
									{isUpdating && (
										<Icon spin className="mr-3" icon={faSpinnerThird} />
									)}
									{t(
										'pages.addshipment.manual.modal.edit-default-value.save-values',
										'Update',
									)}
								</Button>
							</div>
						</DialogFooter>
					</form>
				</Form>
				<AlertDialog
					open={valuesPendingConfirmation !== null}
					onOpenChange={(open) => {
						if (!open) {
							setValuesPendingConfirmation(null);
						}
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t(
									'pages.addshipment.manual.modal.edit-default-value.no-preference.title',
									'Save with missing prefilled values?',
								)}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(
									'pages.addshipment.manual.modal.edit-default-value.no-preference.description',
									'Where no preference is selected, your organisation’s default settings will apply.',
								)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel
								onClick={() => setValuesPendingConfirmation(null)}
							>
								{t(
									'pages.addshipment.manual.modal.edit-default-value.no-preference.cancel',
									'Keep editing',
								)}
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => {
									if (valuesPendingConfirmation === null) {
										return;
									}

									save(valuesPendingConfirmation);
									setValuesPendingConfirmation(null);
								}}
							>
								{t(
									'pages.addshipment.manual.modal.edit-default-value.no-preference.confirm',
									'Save anyway',
								)}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</DialogContent>
		</Dialog>
	);
}
